import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Mail, Paperclip, X, Loader2, Settings } from 'lucide-react';

export const SendEmailModal = ({ open, onOpenChange, lead, onEmailSent }) => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [formData, setFormData] = useState({
    to_email: '',
    subject: '',
    body: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  // Load templates and email status
  const loadData = useCallback(async () => {
    try {
      const [templatesData, statusData] = await Promise.all([
        get('/email-templates'),
        get('/email/status')
      ]);
      setTemplates(templatesData);
      setEmailStatus(statusData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, [get]);

  useEffect(() => {
    if (open) {
      loadData();
      // Pre-fill email if lead has one
      if (lead?.email) {
        setFormData(prev => ({ ...prev, to_email: lead.email }));
      }
    }
  }, [open, loadData, lead]);

  // Apply template
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId === 'none') {
      setFormData(prev => ({ ...prev, subject: '', body: '' }));
      return;
    }
    
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Replace placeholders with lead data
      let subject = template.subject;
      let body = template.body;
      
      if (lead) {
        const replacements = {
          '{{firma}}': lead.firma || '',
          '{{ansprechpartner}}': lead.ansprechpartner || '',
          '{{rolle}}': lead.rolle || '',
          '{{email}}': lead.email || '',
          '{{standort}}': lead.standort || '',
          '{{branche}}': lead.branche || ''
        };
        
        Object.entries(replacements).forEach(([placeholder, value]) => {
          subject = subject.replace(new RegExp(placeholder, 'g'), value);
          body = body.replace(new RegExp(placeholder, 'g'), value);
        });
      }
      
      setFormData(prev => ({ ...prev, subject, body }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailStatus?.configured) {
      toast.error('E-Mail ist nicht konfiguriert');
      return;
    }
    
    if (!formData.to_email || !formData.subject || !formData.body) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setSending(true);
    
    try {
      // Get token for auth
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Use FormData for multipart/form-data request
      const formDataToSend = new FormData();
      formDataToSend.append('lead_id', lead.id);
      formDataToSend.append('to_email', formData.to_email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('body', formData.body);
      if (selectedTemplateId && selectedTemplateId !== 'none') {
        formDataToSend.append('template_id', selectedTemplateId);
      }
      
      // Add attachments
      attachments.forEach(file => {
        formDataToSend.append('attachments', file);
      });
      
      const response = await fetch(`${API_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });
      
      // Read response body once
      const responseData = await response.json();
      
      if (!response.ok) {
        // Check for specific SMTP authentication error
        const errorMsg = responseData.detail || '';
        if (errorMsg.includes('535') || errorMsg.includes('Authentication')) {
          throw new Error('SMTP-Authentifizierung fehlgeschlagen. Bitte prüfen Sie die E-Mail-Zugangsdaten im Admin-Bereich oder aktivieren Sie "SMTP AUTH" in Microsoft 365.');
        }
        throw new Error(errorMsg || 'E-Mail konnte nicht gesendet werden');
      }
      
      toast.success(responseData.message || 'E-Mail erfolgreich gesendet');
      
      // Reset form
      setFormData({ to_email: lead?.email || '', subject: '', body: '' });
      setAttachments([]);
      setSelectedTemplateId('');
      onOpenChange(false);
      
      if (onEmailSent) {
        onEmailSent();
      }
    } catch (error) {
      toast.error(error.message || 'Fehler beim Senden der E-Mail');
    } finally {
      setSending(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#E63946]" />
            E-Mail senden
          </DialogTitle>
          <DialogDescription>
            Senden Sie eine E-Mail an {lead?.ansprechpartner} von {lead?.firma}
          </DialogDescription>
        </DialogHeader>
        
        {!emailStatus?.configured && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-sm text-yellow-800">
            <p className="font-medium mb-2">E-Mail ist nicht konfiguriert</p>
            <p className="mb-3">Bitte konfigurieren Sie zuerst Ihre E-Mail-Einstellungen, um E-Mails versenden zu können.</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                onOpenChange(false);
                navigate('/email-settings');
              }}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Zu den E-Mail-Einstellungen
            </Button>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="send-email-form">
          {/* Template Selection */}
          <div className="space-y-2">
            <Label>Vorlage (optional)</Label>
            <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
              <SelectTrigger data-testid="template-select">
                <SelectValue placeholder="Vorlage auswählen..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Keine Vorlage</SelectItem>
                {templates.map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                    {template.kategorie && ` (${template.kategorie})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* To Email */}
          <div className="space-y-2">
            <Label>An *</Label>
            <Input
              type="email"
              value={formData.to_email}
              onChange={(e) => setFormData({ ...formData, to_email: e.target.value })}
              placeholder="empfaenger@beispiel.de"
              required
              data-testid="email-to-input"
            />
          </div>
          
          {/* Subject */}
          <div className="space-y-2">
            <Label>Betreff *</Label>
            <Input
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="E-Mail-Betreff"
              required
              data-testid="email-subject-input"
            />
          </div>
          
          {/* Body */}
          <div className="space-y-2">
            <Label>Nachricht *</Label>
            <textarea
              className="w-full min-h-[200px] p-3 border border-gray-200 rounded-md focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              placeholder="Ihre Nachricht..."
              required
              data-testid="email-body-input"
            />
          </div>
          
          {/* Attachments */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Anhänge
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                multiple
                onChange={handleFileChange}
                className="cursor-pointer"
                data-testid="email-attachments-input"
              />
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {attachments.map((file, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
                  >
                    <span className="truncate max-w-[150px]">{file.name}</span>
                    <span className="text-gray-400">({formatFileSize(file.size)})</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* From Info */}
          {emailStatus?.configured && (
            <div className="text-sm text-gray-500 border-t pt-4">
              Von: {emailStatus.from_name} &lt;{emailStatus.from_email}&gt;
            </div>
          )}
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              className="bg-[#E63946] hover:bg-[#D62839]"
              disabled={sending || !emailStatus?.configured}
              data-testid="send-email-button"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sende...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Senden
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
